'use client';

import React from 'react';
import { styled } from '@linaria/react';

import useScrollPosition from '@/hooks/use-scroll-position';
import useWindowDimensions from '@/hooks/use-window-dimensions';

import ConnectedLikeButton from '@/components/LikeButton/ConnectedLikeButton';
import ConnectedLikeCount from '@/components/LikeButton/ConnectedLikeCount';

const MINIMUM_BUFFER = 300;

interface Props {
  maxArticleWidth: number;
}

const FloatingSidebar = ({ maxArticleWidth }: Props) => {
  const [, scrollY] = useScrollPosition();
  const { width: windowWidth } = useWindowDimensions();

  // The tricky thing here is that different articles have different maximum widths. I used to dynamically generate the media query using the `maxArticleWidth` prop, but that doesn’t work with Linaria / static CSS-in-JS.
  // So here’s what I need to do instead: we’ll track the window width in state, and derive whether it’s visible or not based on that value.
  const maxArticleWidthInPixels = maxArticleWidth * 16;
  const isVisible =
    typeof windowWidth === 'number' &&
    typeof scrollY === 'number' &&
    scrollY >= 250 &&
    windowWidth > maxArticleWidthInPixels + MINIMUM_BUFFER;

  const shiftBy = maxArticleWidth / 2 + 4;

  return (
    <Wrapper
      style={{
        '--shift-by': `${shiftBy}rem`,
        opacity: isVisible ? 1 : 0,
        visibility: isVisible ? 'visible' : 'hidden',
        transition: isVisible
          ? `opacity 500ms, visibility 0ms`
          : `opacity 500ms, visibility 0ms 500ms`,
      }}
    >
      <ConnectedLikeButton />
      <ConnectedLikeCount />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: fixed;
  top: 16rem;
  left: calc(50vw + var(--shift-by));
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

export default FloatingSidebar;
